import CampaignIcon from '@mui/icons-material/Campaign';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRouter } from 'next/router'; // Add this import
import React, { useState } from 'react';
import { submitCampaign } from 'src/services/api'; // Import the API function

const StyledHero = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  minHeight: '40vh',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(3, 0),
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('lg')]: {
    minHeight: '50vh',
    padding: theme.spacing(2, 0),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("/campaign/images/client-area/pattern.png")',
    opacity: 0.1,
    animation: 'float 20s linear infinite',
  },
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.grey[100]} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: '3rem',
  fontWeight: 800,
  lineHeight: 1.2,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('lg')]: {
    fontSize: '2.2rem',
    textAlign: 'center',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1.8rem',
  },
}));

const AnimatedBox = styled(Box)({
  animation: 'fadeInUp 0.8s ease-out',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const StyledButton = styled(Button)(() => ({
  borderRadius: '30px',
  padding: '10px 24px', // Reduced padding
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
  },
}));

const Hero = () => {
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: '',
    contactNumber: '',
    emailAddress: '',
    goal: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const router = useRouter();

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.organizationName)
      newErrors.organizationName = 'Organization name is required';
    const phoneRegex =
      /^(\+1|1)?[-. ]?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    if (!phoneRegex.test(formData.contactNumber))
      newErrors.contactNumber = 'Enter a valid US phone number';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.emailAddress))
      newErrors.emailAddress = 'Enter a valid email address';
    if (!formData.goal || Number.parseFloat(formData.goal) <= 0)
      newErrors.goal = 'Goal must be a positive number';
    return newErrors;
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');

    // Format the number as user types
    let formattedNumber = '';
    if (phoneNumber.length <= 3) {
      formattedNumber = phoneNumber;
    } else if (phoneNumber.length <= 6) {
      formattedNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      formattedNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6)}`;
    } else {
      formattedNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6, 10)}`;
    }

    return formattedNumber;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'contactNumber') {
      // Format phone number as user types
      const formattedNumber = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formattedNumber }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      await submitCampaign(formData);
      handleCloseModal();
      setSnackbar({
        open: true,
        message: 'Campaign creation requested successfully!',
        severity: 'success',
      });
      setFormData({
        organizationName: '',
        contactNumber: '',
        emailAddress: '',
        goal: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create campaign. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleViewCampaigns = () => {
    router.push('/allcampaigns');
  };

  return (
    <StyledHero>
      <Container maxWidth="lg">
        <Grid alignItems="center" container spacing={4}>
          <Grid
            size={{
              md: 6,
              xs: 12
            }}>
            <AnimatedBox>
              <GradientText variant="h1">Amplify Your Impact</GradientText>
              <Typography
                color="common.white"
                sx={{
                  mb: 6,
                  mt: 3,
                  opacity: 0.9,
                  lineHeight: 1.8,
                  textAlign: { xs: 'center', md: 'left' },
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  letterSpacing: '0.02em',
                }}
                variant="h5"
              >
                Launch powerful fundraising campaigns
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  mt: 2,
                }}
              >
                <StyledButton
                  color="secondary"
                  endIcon={<LaunchIcon />}
                  onClick={handleOpenModal}
                  size="large"
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.5, md: 2 },
                  }}
                  variant="contained"
                >
                  Start Your Campaign
                </StyledButton>
                <StyledButton
                  onClick={handleViewCampaigns}
                  size="large"
                  startIcon={<CampaignIcon />}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.5, md: 2 },
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                  variant="outlined"
                >
                  Live Campaigns
                </StyledButton>
              </Box>
            </AnimatedBox>
          </Grid>
        </Grid>
      </Container>
      {/* Campaign Modal */}
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={handleCloseModal}
        open={openModal}
      >
        <DialogTitle>Start Your Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              error={!!errors.organizationName}
              fullWidth
              helperText={errors.organizationName}
              label="Organization Name"
              name="organizationName"
              onChange={handleChange}
              value={formData.organizationName}
              variant="outlined"
            />
            <TextField
              error={!!errors.contactNumber}
              fullWidth
              helperText={errors.contactNumber || 'Example: (555) 123-4567'}
              label="Contact Number"
              name="contactNumber"
              onChange={handleChange}
              placeholder="(555) 123-4567"
              value={formData.contactNumber}
              variant="outlined"
              slotProps={{
                htmlInput: {
                  maxLength: 14, // (XXX) XXX-XXXX
                }
              }}
            />
            <TextField
              error={!!errors.emailAddress}
              fullWidth
              helperText={errors.emailAddress}
              label="Email Address"
              name="emailAddress"
              onChange={handleChange}
              type="email"
              value={formData.emailAddress}
              variant="outlined"
            />
            <TextField
              error={!!errors.goal}
              fullWidth
              helperText={errors.goal}
              label="Goal Amount ($)"
              name="goal"
              onChange={handleChange}
              type="number"
              value={formData.goal}
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button color="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        open={snackbar.open}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledHero>
  );
};

export default Hero;
